"use client"
import type { FragmentOf } from "@opensea/graphql"
import { readFragment } from "@opensea/graphql"
import {
  BottomSheetBody,
  BottomSheetFooter,
  BottomSheetHeader,
  BottomSheetHeaderTitle,
  Flex,
  FlexCenter,
  Form,
  Separator,
} from "@opensea/ui-kit"
import { useState } from "react"
import { FormProvider, useForm, useWatch } from "react-hook-form"
import { useTranslations } from "use-intl"
import { BackButton } from "../../../components/BackButton/BackButton"
import { Button } from "../../../components/Button"
import { useAllActionsCompleted } from "../../hooks/useAllActionsCompleted"
import { useSetBlockchainActionErrors } from "../../hooks/useSetBlockchainActionErrors"
import { CancelOrderFlowContentFragment } from "./CancelOrderFlowContent.fragment"
import { CancelOrderActionTimeline } from "./components/CancelOrderActionTimeline/CancelOrderActionTimeline"
import { CancelOrderFlowStatus } from "./components/CancelOrderFlowStatus"
import { CancelOrderTable } from "./components/CancelOrderTable"
import { GaslessCancelOrderStatus } from "./components/GaslessCancelOrderStatus"
import type { CancelOrderFlowView, GaslessCancelSignatureMap } from "./types"

type Props = {
  data: FragmentOf<typeof CancelOrderFlowContentFragment>
  gaslessCancelEligibleOrders?: boolean
  onForceOnchainCancellation?: () => void
}

export const CancelOrderFlowContent = ({
  data: dataKey,
  gaslessCancelEligibleOrders,
  onForceOnchainCancellation,
}: Props) => {
  const t = useTranslations("wallet.CancelOrderFlow")
  const data = readFragment(CancelOrderFlowContentFragment, dataKey)
  const [view, setView] = useState<CancelOrderFlowView>("review")

  const [gaslessCancelSignatureMap, setGaslessCancelSignatureMap] = useState<
    GaslessCancelSignatureMap | undefined
  >()

  useSetBlockchainActionErrors(data.errors)

  const [allActionsCompleted] = useAllActionsCompleted()

  const actions = data.actions

  const cancelOrderAction = actions.flatMap(a =>
    a.__typename === "CancelOrdersAction" ? [a] : [],
  )[0]
  const gaslessCancelOrderAction = actions.flatMap(a =>
    a.__typename === "GaslessCancelOrdersAction" ? [a] : [],
  )[0]
  const hasGaslessCancelOrders =
    gaslessCancelEligibleOrders && !!gaslessCancelOrderAction
  const orders = hasGaslessCancelOrders
    ? gaslessCancelOrderAction.orders
    : cancelOrderAction.orders

  const methods = useForm({
    defaultValues: {
      orders: orders.map(o => ({ id: o.id })),
    },
    mode: "onChange",
  })

  const { handleSubmit } = methods

  const formOrders = useWatch({ control: methods.control, name: "orders" })

  const onSubmit = () => {
    setView("review")
  }

  if (!orders.length) {
    return null
  }

  const tableOrders = formOrders.flatMap(formOrder => {
    const matchedOrder = orders.find(order => order.id === formOrder.id)

    return matchedOrder ? [matchedOrder] : []
  })

  if (view === "done" && !hasGaslessCancelOrders && allActionsCompleted) {
    return (
      <FormProvider {...methods}>
        <CancelOrderFlowStatus orders={orders} />
      </FormProvider>
    )
  }
  if (view === "done" && hasGaslessCancelOrders) {
    return (
      <FormProvider {...methods}>
        <GaslessCancelOrderStatus
          action={gaslessCancelOrderAction}
          forceOnchainCancellation={() => {
            setGaslessCancelSignatureMap(undefined)
            onForceOnchainCancellation?.()
            setView("cancel")
          }}
          signatureMap={gaslessCancelSignatureMap}
        />
      </FormProvider>
    )
  }

  return (
    <FormProvider {...methods}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <BottomSheetHeader>
          <BottomSheetHeaderTitle>
            <FlexCenter className="gap-3">
              {view === "review" ? (
                <>
                  <BackButton onClick={() => setView("cancel")} />
                  <Separator
                    className="h-4 text-secondary"
                    orientation="vertical"
                  />
                </>
              ) : null}

              <h3>
                {t(
                  orders[0].side === "LISTING"
                    ? "cancelListing"
                    : "cancelOffer",
                  { count: orders.length },
                )}
              </h3>
            </FlexCenter>
          </BottomSheetHeaderTitle>
        </BottomSheetHeader>

        <BottomSheetBody>
          <CancelOrderTable orders={tableOrders} view={view} />
        </BottomSheetBody>

        <BottomSheetFooter>
          {view === "cancel" ? (
            <Flex className="justify-end">
              <Button type="submit" variant="destructive">
                {t(
                  orders[0].side === "LISTING"
                    ? "cancelListing"
                    : "cancelOffer",
                  { count: orders.length },
                )}
              </Button>
            </Flex>
          ) : null}
          {view === "review" ? (
            <CancelOrderActionTimeline
              gaslessCancelEligibleOrders={Boolean(gaslessCancelEligibleOrders)}
              orderIds={tableOrders.map(o => o.id)}
              setGaslessCancelSignatureMap={setGaslessCancelSignatureMap}
              setView={setView}
            />
          ) : null}
        </BottomSheetFooter>
      </Form>
    </FormProvider>
  )
}
